import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { MappedComponent } from "../helpers/types";
import ValidationResults from "../helpers/validationResults";

export interface ValidationContextType {
	hideValidation?: boolean;
	setValidationTriggered?: () => void;
	clearValidationTriggered?: () => void;
	validateOnFocus?: boolean;
	validationResults: ValidationResults;
	validateOnSave?: boolean;
	validateComponentIf?: (component: MappedComponent, shownByRule?: boolean) => boolean;
}

const defaultContextValues = {
	hideValidation: false,
	validateOnFocus: false,
	setValidationTriggered: () => {},
	clearValidationTriggered: () => {},
	validationResults: null,
	validateComponentIf: () => true,
} as ValidationContextType;
interface ValidationContextProps {
	children: ReactNode;
	hideValidation?: boolean;
	validateOnFocus?: boolean;
	/** If true, only show validation when try to save (applies to web view; mobile has its own solution) */
	validateOnSave?: boolean;
	validationResults: ValidationResults;
	validateComponentIf?: (component: MappedComponent, shownByRule?: boolean) => boolean;
}

export const ValidationContext = createContext<ValidationContextType>(defaultContextValues);
export const useValidationContext = () => useContext(ValidationContext);
export function ValidationProvider(props: ValidationContextProps) {
	const { children } = props;
	const { validationResults, validateOnSave, validateComponentIf } = props;
	const [hideValidation, setHideValidation] = useState(!!validateOnSave);
	const validateOnFocus = useMemo(
		() => props.validateOnFocus && hideValidation,
		[props.validateOnFocus, hideValidation],
	);
	const setValidationTriggered = useCallback(() => {
		setHideValidation(false);
	}, [setHideValidation]);
	const clearValidationTriggered = useCallback(() => {
		setHideValidation(true);
	}, [setHideValidation]);

	const state = useMemo(
		() => ({
			hideValidation,
			validateOnFocus,
			validationResults,
			setValidationTriggered,
			clearValidationTriggered,
			validateOnSave,
			validateComponentIf,
		}),
		[
			hideValidation,
			validateOnFocus,
			validateOnSave,
			validationResults,
			setValidationTriggered,
			clearValidationTriggered,
			validateComponentIf,
		],
	);
	return <ValidationContext.Provider value={state}>{children}</ValidationContext.Provider>;
}
